/**
 * Single-editor lock for the program builder (AI Structure / Content and the
 * Manual editor), shared by collaborating admins.
 *
 * The provider sits ABOVE the builder so remounting the builder (e.g. to load
 * the latest changes) never drops the lock. The builder tells it what it is
 * editing via useEditLockTarget(grpId, stage) — stage null (e.g. the chat
 * stages) means "nothing lockable on screen" and releases it.
 *
 *   held    → this admin is the editor; heartbeat every 30s (the BE lock
 *             expires 90s after the last one)
 *   locked  → someone else is; the builder renders read-only and we retry
 *             every 15s, so it unlocks by itself once they leave
 *
 * The lock itself is the course-versioning one (program_edit_locks, POST
 * programs/{grpId}/edit-lock), which the BE also enforces on draft, content
 * and restore writes.
 *
 * Fails open: if the lock endpoint errors (e.g. BE not deployed yet), the
 * builder stays editable rather than locking the admin out of their own work.
 *
 * Pass `grpId` when the program is known at mount: the provider then starts
 * as 'acquiring' instead of 'idle', so nothing writes (or auto-generates) on
 * the first render, before the lock answer is back. The status is mirrored
 * into the buildState write gate for code that can't read this context.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../../api';
import type { EditLockStage, ProgramEditLock } from '../../api';
import { setEditLockGateStatus } from './buildStateWriteGate';

const HEARTBEAT_MS = 30_000;
const RETRY_WHEN_LOCKED_MS = 15_000;

export type EditLockStatus = 'idle' | 'acquiring' | 'held' | 'locked';

interface EditLockContextValue {
  status: EditLockStatus;
  /** Who holds the lock when status is 'locked'. */
  holder: ProgramEditLock | null;
  /** Bumps each time the lock is regained after someone else held it. */
  regainedCount: number;
  grpId: string;
  setTarget: (grpId: string, stage: EditLockStage | null) => void;
}

const EditLockContext = createContext<EditLockContextValue | null>(null);

export const ProgramEditLockProvider: React.FC<{ children: React.ReactNode; grpId?: string }> = ({
  children,
  grpId: initialGrpId = ''
}) => {
  // `declared` flips once the builder has said what it is editing; until then
  // a known program stays 'acquiring' so the first render can't write.
  const [target, setTargetState] = useState<{ grpId: string; stage: EditLockStage | null; declared: boolean }>({
    grpId: initialGrpId,
    stage: null,
    declared: false
  });
  const initialStatus: EditLockStatus = initialGrpId ? 'acquiring' : 'idle';
  const [status, setStatus] = useState<EditLockStatus>(initialStatus);
  const [holder, setHolder] = useState<ProgramEditLock | null>(null);
  const [regainedCount, setRegainedCount] = useState(0);

  const statusRef = useRef<EditLockStatus>(initialStatus);
  const stageRef = useRef<EditLockStage | null>(null);
  stageRef.current = target.stage;

  const setTarget = useCallback((grpId: string, stage: EditLockStage | null) => {
    setTargetState((prev) =>
      prev.declared && prev.grpId === grpId && prev.stage === stage ? prev : { grpId, stage, declared: true }
    );
  }, []);

  // Mirror the status into the write gate (keyed by program) for persistence
  // code that sits above this provider, e.g. ProgramCreationContext.
  const gateGrpId = target.grpId || initialGrpId;
  useEffect(() => {
    if (!gateGrpId) return undefined;
    setEditLockGateStatus(gateGrpId, status);
    return () => setEditLockGateStatus(gateGrpId, null);
  }, [gateGrpId, status]);

  const active = Boolean(target.grpId && target.stage);

  const acquire = useCallback(async (grpId: string) => {
    const stage = stageRef.current;
    if (!stage) return;
    try {
      const data = await API.programEditLock(grpId, {
        action: statusRef.current === 'held' ? 'heartbeat' : 'acquire',
        stage
      });
      if (!data) throw new Error('empty edit-lock response');
      const wasLocked = statusRef.current === 'locked';
      const next: EditLockStatus = data.acquired ? 'held' : 'locked';
      statusRef.current = next;
      setStatus(next);
      setHolder(data.acquired ? null : data.lock);
      if (wasLocked && data.acquired) setRegainedCount((n) => n + 1);
    } catch (err) {
      console.warn('[editLock] acquire failed — leaving the builder editable', err);
      if (statusRef.current !== 'locked') {
        statusRef.current = 'held';
        setStatus('held');
      }
    }
  }, []);

  // Lifecycle per program: acquire, keep alive / retry, release on leave.
  useEffect(() => {
    if (!active) {
      // Not declared yet: keep the initial 'acquiring' for a known program.
      if (!target.declared) return undefined;
      statusRef.current = 'idle';
      setStatus('idle');
      setHolder(null);
      return undefined;
    }
    const grpId = target.grpId;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loop = async () => {
      await acquire(grpId);
      if (cancelled) return;
      timer = setTimeout(loop, statusRef.current === 'locked' ? RETRY_WHEN_LOCKED_MS : HEARTBEAT_MS);
    };
    statusRef.current = 'acquiring';
    setStatus('acquiring');
    loop();

    const release = () => {
      if (statusRef.current === 'held') API.programEditLock(grpId, { action: 'release' }).catch(() => undefined);
    };
    // Tab close / reload / navigating away from the SPA.
    const onPageHide = () => release();
    // Restored from the back-forward cache: take it back.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) acquire(grpId);
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      release();
      statusRef.current = 'idle';
    };
  }, [active, target.declared, target.grpId, acquire]);

  // Moving Structure → Content re-stamps the stage without releasing.
  useEffect(() => {
    if (active && statusRef.current === 'held') acquire(target.grpId);
  }, [target.stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({ status, holder, regainedCount, grpId: target.grpId, setTarget }),
    [status, holder, regainedCount, target.grpId, setTarget]
  );
  return <EditLockContext.Provider value={value}>{children}</EditLockContext.Provider>;
};

/** Lock state for the surrounding builder; inert outside a provider. */
export const useEditLock = (): EditLockContextValue => {
  const ctx = useContext(EditLockContext);
  return (
    ctx || {
      status: 'idle',
      holder: null,
      regainedCount: 0,
      grpId: '',
      setTarget: () => undefined
    }
  );
};

/** Declare what the builder is editing right now (stage null = nothing lockable). */
export const useEditLockTarget = (grpId: string | undefined, stage: EditLockStage | null) => {
  const { setTarget } = useEditLock();
  useEffect(() => {
    setTarget(grpId || '', grpId ? stage : null);
  }, [grpId, stage, setTarget]);
};

/** True while another admin holds the lock — the builder must not write. */
export const useBuilderReadOnly = (): boolean => useEditLock().status === 'locked';

/**
 * True when this copy of the builder may write to the BE or kick off a
 * generation: no lock provider around, the lock is held, or nothing lockable
 * is on screen. False while acquiring (the copy may be stale) or locked.
 */
export const useBuilderWritable = (): boolean => {
  const ctx = useContext(EditLockContext);
  return !ctx || ctx.status === 'held' || ctx.status === 'idle';
};
